/**
 * Typed wrappers around the admin players / pairing / assignments
 * REST endpoints. The server controllers live at:
 *
 *   GET    /api/v1/admin/players                  (list, optional ?group_id=&state=)
 *   GET    /api/v1/admin/players/:id              (detail)
 *   PATCH  /api/v1/admin/players/:id              (rename / move group)
 *   POST   /api/v1/admin/players/:id/revoke       (revoke)
 *   POST   /api/v1/admin/pairing/init             (create unpaired slot + code)
 *   POST   /api/v1/admin/assignments              (player- or group-level)
 *   GET    /api/v1/admin/groups/tree              (group tree for pickers)
 *
 * Notes on server contracts (verified):
 *   - GET /admin/players returns `{players: PlayerSummary[]}` only —
 *     no `total`/`page`/`per_page`. Phase-1 fleet size is small so the
 *     server doesn't paginate. The page/perPage parameters in
 *     `listPlayers()` are accepted by the wrapper for forward-compat
 *     but currently ignored by the server; client-side pagination is
 *     applied in the page component.
 *   - POST /admin/players/:id/revoke returns the updated PlayerSummary
 *     (not 204).
 *   - POST /admin/assignments accepts `player_id | group_id` xor
 *     `composer_id` and optional `effective_from` / `effective_until`
 *     ISO strings.
 */

import type {
  DeviceConfigValues,
  DeviceTelemetry,
  DeviceType,
  EffectiveDeviceConfig,
  PairingInitInput,
  PairingInitResponse,
  PairingState,
  PlayerClassOverride,
  PlayerGroupNode,
  PlayerAuditEntry,
  PlayerSummary,
  SetLifecycleBody,
  SetGpsBody,
} from '@displaycms/contracts';

import { apiGet, apiPatch, apiPost, apiPut } from '@/api/client';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface PlayerListQuery {
  group_id?: string | undefined;
  state?: PairingState | undefined;
  /** Accepted for API symmetry; server ignores in Phase 1. */
  page?: number | undefined;
  /** Accepted for API symmetry; server ignores in Phase 1. */
  per_page?: number | undefined;
}

export interface PlayerListResponse {
  players: PlayerSummary[];
}

function buildListQuery(query: PlayerListQuery): string {
  const params = new URLSearchParams();
  if (query.group_id) params.set('group_id', query.group_id);
  if (query.state) params.set('state', query.state);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.per_page !== undefined) params.set('per_page', String(query.per_page));
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export interface ListPlayersArgs {
  group?: string | undefined;
  state?: PairingState | undefined;
  page?: number | undefined;
  perPage?: number | undefined;
}

export function listPlayers(
  args: ListPlayersArgs = {},
  options?: { signal?: AbortSignal },
): Promise<PlayerListResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  const query: PlayerListQuery = {};
  if (args.group !== undefined) query.group_id = args.group;
  if (args.state !== undefined) query.state = args.state;
  if (args.page !== undefined) query.page = args.page;
  if (args.perPage !== undefined) query.per_page = args.perPage;
  return apiGet<PlayerListResponse>(
    `/v1/admin/players${buildListQuery(query)}`,
    opts,
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function getPlayer(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<PlayerSummary> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<PlayerSummary>(`/v1/admin/players/${id}`, opts);
}

// ---------------------------------------------------------------------------
// Update / Revoke
// ---------------------------------------------------------------------------

export interface PlayerUpdateInput {
  display_name?: string | undefined;
  group_id?: string | null | undefined;
  /** SP2: the operator-set screen type (drives the type icon). */
  device_type?: DeviceType | undefined;
}

export function updatePlayer(
  id: string,
  patch: PlayerUpdateInput,
): Promise<PlayerSummary> {
  return apiPatch<PlayerSummary>(`/v1/admin/players/${id}`, patch);
}

/** Screen-inventory (SP2): set the admin lifecycle (maintenance/broken/end_of_life,
 *  or back to active for a broken screen). broken/EOL unpair the device server-side. */
export function setPlayerLifecycle(
  id: string,
  body: SetLifecycleBody,
): Promise<PlayerSummary> {
  return apiPatch<PlayerSummary>(`/v1/admin/players/${id}/lifecycle`, body);
}

/** Screen-inventory (SP2): set a manual GPS pin (lat/lng) or clear it
 *  ({lat:null,lng:null} → reverts to device-reported). Manual wins over device. */
export function setPlayerGps(id: string, body: SetGpsBody): Promise<PlayerSummary> {
  return apiPatch<PlayerSummary>(`/v1/admin/players/${id}/gps`, body);
}

export function revokePlayer(id: string): Promise<PlayerSummary> {
  return apiPost<PlayerSummary>(`/v1/admin/players/${id}/revoke`);
}

/** Screen-inventory (SP2 fast-follow): a screen's lifecycle history for the
 *  detail-page timeline (newest first). */
export function getPlayerAudit(id: string): Promise<PlayerAuditEntry[]> {
  return apiGet<PlayerAuditEntry[]>(`/v1/admin/players/${id}/audit`);
}

/**
 * Operator override of a player's hardware class and/or capability
 * flags. The server discriminates the PATCH body: presence of any of
 * `reason` / `capability_flags` / `player_class` routes to the override
 * path (requires `players.assign`). `reason` is mandatory (≥3 chars) and
 * at least one of `player_class` / `capability_flags` must be present —
 * the caller is responsible for only sending changed fields.
 */
export function overridePlayerClass(
  id: string,
  body: PlayerClassOverride,
): Promise<PlayerSummary> {
  return apiPatch<PlayerSummary>(`/v1/admin/players/${id}`, body);
}

// ---------------------------------------------------------------------------
// Pairing
// ---------------------------------------------------------------------------

export function initPairing(input: PairingInitInput): Promise<PairingInitResponse> {
  return apiPost<PairingInitResponse>('/v1/admin/pairing/init', input);
}

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

export interface AssignmentCreateOptions {
  // P3 W2 B9 (FS §8.5) — audited capability-mismatch override. Both
  // keys are conditionally spread onto the request body ONLY when the
  // caller passes them, so a non-force assign is BYTE-IDENTICAL to the
  // W3 T6 wire shape (`{ player_id, composer_id, starts_at, ends_at: null,
  // rrule: null, priority: 999 }`). The server's zod refine enforces
  // `reason` (min 3 chars) iff `force === true`.
  force?: boolean | undefined;
  reason?: string | undefined;
}

// Return shape mirrors ScheduledAssignmentRow (the actual server response
// from POST /v1/admin/scheduled-assignments). The caller (detail.tsx
// assignMutation.onSuccess) does not inspect any field on the response —
// it only checks success/failure — so the shape is advisory.
export interface AssignmentRow {
  id: string;
  player_id: string | null;
  group_id: string | null;
  composer_id: string;
  starts_at: string;
  ends_at: string | null;
  priority: number;
}

// W3 T6: flipped from the deleted /admin/assignments to the live
// /admin/scheduled-assignments endpoint. Non-force body mirrors the
// spec's T6 wire shape exactly (starts_at=now, ends_at=null, rrule=null,
// priority=999). force/reason are conditionally spread when passed.
export function assignToPlayer(
  playerId: string,
  composerId: string,
  options: AssignmentCreateOptions = {},
): Promise<AssignmentRow> {
  const body: Record<string, unknown> = {
    player_id: playerId,
    composer_id: composerId,
    starts_at: new Date().toISOString(),
    ends_at: null,
    rrule: null,
    priority: 999,
  };
  if (options.force !== undefined) body['force'] = options.force;
  if (options.reason !== undefined) body['reason'] = options.reason;
  return apiPost<AssignmentRow>('/v1/admin/scheduled-assignments', body);
}

// ---------------------------------------------------------------------------
// Device commands (W2 supervision panel)
// ---------------------------------------------------------------------------

export type DeviceCommandType =
  | 'device.relaunch'
  | 'device.clear_cache'
  | 'device.set_volume'
  | 'device.reboot'
  | 'device.screenshot';

export function issueDeviceCommand(
  playerId: string,
  body: { command_type: DeviceCommandType; args?: Record<string, unknown>; rationale?: string },
): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/players/${playerId}/device-command`, body);
}

// ---------------------------------------------------------------------------
// Telemetry
// ---------------------------------------------------------------------------

export interface PlayerTelemetryView {
  latest: DeviceTelemetry | null;
  history: DeviceTelemetry[];
}

export function getPlayerTelemetry(playerId: string): Promise<PlayerTelemetryView> {
  return apiGet<PlayerTelemetryView>(`/v1/admin/players/${playerId}/telemetry`);
}

// ---------------------------------------------------------------------------
// Groups (picker)
// ---------------------------------------------------------------------------

export interface GroupsTreeResponse {
  tree: PlayerGroupNode[];
}

export function listGroupsTree(
  options?: { signal?: AbortSignal },
): Promise<GroupsTreeResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<GroupsTreeResponse>('/v1/admin/groups/tree', opts);
}

// ---------------------------------------------------------------------------
// Device config (P4 W3 T5)
// ---------------------------------------------------------------------------

/**
 * Shape returned by GET /api/v1/admin/players/:id/config.
 * `device` is the device-scope row (subset of Prisma fields we use) or null.
 * `effective` is the merged effective config (all scopes: tenant → group → device).
 */
export interface PlayerConfigView {
  device: { values: DeviceConfigValues; version: number } | null;
  effective: EffectiveDeviceConfig;
}

/** GET /api/v1/admin/players/:id/config — requires players.read */
export function getPlayerConfig(playerId: string): Promise<PlayerConfigView> {
  return apiGet<PlayerConfigView>(`/v1/admin/players/${playerId}/config`);
}

/**
 * PUT /api/v1/admin/players/:id/config — requires players.assign.
 * Returns the updated EffectiveDeviceConfig (config_version + merged values).
 */
export function putPlayerConfig(
  playerId: string,
  values: DeviceConfigValues,
): Promise<EffectiveDeviceConfig> {
  return apiPut<EffectiveDeviceConfig>(`/v1/admin/players/${playerId}/config`, {
    values,
  });
}

// ---------------------------------------------------------------------------
// Provisioning QR (P4 W3 T6)
// ---------------------------------------------------------------------------

/**
 * Shape returned by POST /api/v1/admin/players/provisioning/qr.
 * `qr_payload` is a JSON string to encode in the QR; the others
 * support the ADB bench-provisioning workflow.
 */
export interface ProvisioningQr {
  enrollment_json: Record<string, unknown>;
  qr_payload: string;
  adb_command: string;
  pairing_token: string;
  player_id: string;
}

/** POST /api/v1/admin/players/provisioning/qr — requires players.pair */
export function generateProvisioningQr(body: {
  group_id?: string;
  device_name?: string;
}): Promise<ProvisioningQr> {
  return apiPost<ProvisioningQr>('/v1/admin/players/provisioning/qr', body);
}

/**
 * Flattens the group tree into a `[{id, label, depth}]` array for use in
 * a <select>. Depth determines indentation in the option label.
 */
export interface FlatGroupOption {
  id: string;
  depth: number;
  name_fr: string;
  name_en: string | null;
}

export function flattenGroupsTree(tree: ReadonlyArray<PlayerGroupNode>): FlatGroupOption[] {
  const out: FlatGroupOption[] = [];
  const walk = (nodes: ReadonlyArray<PlayerGroupNode>, depth: number): void => {
    for (const node of nodes) {
      out.push({
        id: node.id,
        depth,
        name_fr: node.name_i18n.fr,
        name_en: node.name_i18n.en ?? null,
      });
      if (node.children.length > 0) {
        walk(node.children, depth + 1);
      }
    }
  };
  walk(tree, 0);
  return out;
}
